// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * What the 3D views keep in memory across a route switch (frontend-38f).
 *
 * The Ground unmounts when the user opens an expert page (Posegraf, Pipeline,
 * …) — they are separate layout routes — and its scene goes with it. What
 * made coming back slow was not the scene but the downloads behind it: every
 * cloud page, tile index and mesh blob was fetched again. So this module keeps
 * the decoded responses, keyed exactly as they were asked for, and the next
 * stream replays them from memory instead of the network; it also keeps the
 * camera each keyed viewport had, so the view comes back where it was.
 *
 * Bounded twice: a byte budget over everything (`budgetFor`, from the
 * device's memory), and at most `MAX_CASES` cases (a case switch is a page
 * load today, so in practice one). A stream that does not fit keeps its
 * prefix rather than thrashing — see {@link ByteLru}.
 *
 * Freshness: entries are keyed by the stream's revision (`clouds.changed`
 * bumps it), and the app's event root drops them outright on
 * `clouds.changed`, on any job that finishes (meshes: a re-import writes the
 * same names) and on an event-socket reconnect (events may have been missed).
 *
 * No React, no DOM, no three.js: the values are opaque to it.
 */

import type { CloudPointsQuery } from '../api/client';

/** Cases kept at once; the least recently used one beyond this is dropped. */
export const MAX_CASES = 2;

/** The memory budget for a device with `deviceMemoryGb` (navigator.deviceMemory; capped at 8 by browsers). */
export function budgetFor(deviceMemoryGb: number | undefined): number {
  const MB = 1024 * 1024;
  // Safari and Firefox do not expose deviceMemory: assume a modest machine.
  if (deviceMemoryGb === undefined || !Number.isFinite(deviceMemoryGb) || deviceMemoryGb <= 0) return 160 * MB;
  if (deviceMemoryGb <= 2) return 128 * MB;
  if (deviceMemoryGb <= 4) return 256 * MB;
  return 512 * MB;
}

interface Entry<V> {
  value: V;
  bytes: number;
  /** Entries of one group (one stream) never evict each other. */
  group: string;
  /** What invalidates it: cloud names, `mesh:<url>`. */
  tags: readonly string[];
  /** The case it belongs to. */
  scope: string;
  /** Insertion order: within a group, the stream order of its pages. */
  seq: number;
}

/**
 * A least-recently-used map bounded by an estimate of bytes.
 *
 * Eviction works on groups (streams), not entries: a cloud is streamed in
 * order, and plain LRU over a stream larger than the budget would evict its
 * first pages to admit its last — then on replay miss the first, refetch it,
 * evict the second, and so on, caching nothing useful. So the victim is the
 * least recently used *other* group, and from it the most recently inserted
 * entry (its tail): every stream keeps a prefix, the part that is on screen
 * first. Once only the inserting stream is left, the new entry is not
 * admitted.
 *
 * `generation` counts invalidations (`deleteWhere`, `clear`). A fetch that
 * started before one must not store its answer after it — it may be the
 * stale data the invalidation was about — so the cached loaders compare the
 * generation they started under before they store.
 */
export class ByteLru<V> {
  private readonly map = new Map<string, Entry<V>>();
  private total = 0;
  private nextSeq = 0;
  private gen = 0;
  /** Case scopes, least recently used first. */
  private scopes: string[] = [];

  constructor(
    private readonly budget: number,
    private readonly maxScopes: number = MAX_CASES,
  ) {}

  /** Bumped by every invalidation. */
  get generation(): number {
    return this.gen;
  }

  get bytes(): number {
    return this.total;
  }

  get size(): number {
    return this.map.size;
  }

  get(key: string): V | undefined {
    const entry = this.map.get(key);
    if (!entry) return undefined;
    // Re-insert: a Map iterates in insertion order, so the front is the LRU.
    this.map.delete(key);
    this.map.set(key, entry);
    this.touchScope(entry.scope);
    return entry.value;
  }

  has(key: string): boolean {
    return this.map.has(key);
  }

  /** Stores `value`; false when it was not admitted (larger than the budget, or its own stream fills it). */
  set(key: string, value: V, bytes: number, opts: { scope: string; group: string; tags?: readonly string[] }): boolean {
    const size = Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
    if (size > this.budget) return false;
    this.delete(key);
    this.touchScope(opts.scope);
    while (this.scopes.length > this.maxScopes) this.dropScope(this.scopes[0]);
    while (this.total + size > this.budget) {
      if (!this.evictOne(opts.group)) return false;
    }
    this.map.set(key, { value, bytes: size, group: opts.group, tags: opts.tags ?? [], scope: opts.scope, seq: this.nextSeq++ });
    this.total += size;
    return true;
  }

  delete(key: string): void {
    const entry = this.map.get(key);
    if (!entry) return;
    this.map.delete(key);
    this.total -= entry.bytes;
  }

  /** Drops every entry `pred` matches. */
  deleteWhere(pred: (entry: { group: string; tags: readonly string[]; scope: string }) => boolean): number {
    this.gen += 1;
    let n = 0;
    for (const [key, entry] of [...this.map]) {
      if (pred(entry)) {
        this.delete(key);
        n += 1;
      }
    }
    return n;
  }

  clear(): void {
    this.gen += 1;
    this.map.clear();
    this.total = 0;
    this.scopes = [];
  }

  /**
   * Evicts the tail (last inserted entry) of the least recently used group
   * other than `protectGroup`; false when there is no other group.
   */
  private evictOne(protectGroup: string): boolean {
    let victim: string | null = null;
    for (const entry of this.map.values()) {
      if (entry.group !== protectGroup) {
        victim = entry.group;
        break;
      }
    }
    if (victim === null) return false;
    let tailKey: string | null = null;
    let tailSeq = -1;
    for (const [key, entry] of this.map) {
      if (entry.group === victim && entry.seq > tailSeq) {
        tailSeq = entry.seq;
        tailKey = key;
      }
    }
    if (tailKey !== null) this.delete(tailKey);
    return tailKey !== null;
  }

  private touchScope(scope: string): void {
    const at = this.scopes.indexOf(scope);
    if (at === this.scopes.length - 1 && at >= 0) return;
    if (at >= 0) this.scopes.splice(at, 1);
    this.scopes.push(scope);
  }

  private dropScope(scope: string): void {
    this.scopes = this.scopes.filter((s) => s !== scope);
    this.deleteWhere((e) => e.scope === scope);
    onScopeDropped?.(scope);
  }
}

let onScopeDropped: ((scope: string) => void) | null = null;

// ------------------------------------------------------------- keys ----

/** A query as a stable key (fixed field order, absent fields left out). */
export function queryKey(query: CloudPointsQuery): string {
  const q = query as Record<string, unknown>;
  return Object.keys(q)
    .filter((k) => q[k] !== undefined)
    .sort()
    .map((k) => `${k}=${String(q[k])}`)
    .join('&');
}

/**
 * One stream's identity: the geometry cloud, the label clouds zipped onto it
 * and the revision the caller bumps on `clouds.changed`. Two streams that
 * differ in any of these never share a page.
 */
export function streamKey(cloud: string, labelKey: string, revision: number, identity = ''): string {
  return `${cloud}|${labelKey}|r${revision}|${identity}`;
}

/**
 * What the server says a cloud is, as far as the list tells (`GET /clouds`:
 * the point count). Part of the stream key, so a cloud rewritten without an
 * event (the CLI on the same file) still misses the cache once its count
 * changed — the list is re-read on every mount.
 */
export function cloudIdentity(clouds: readonly { name: string; point_count: number }[] | null, names: readonly string[]): string {
  return names.map((n) => clouds?.find((c) => c.name === n)?.point_count ?? '?').join(',');
}

/**
 * Same for a mesh (`GET /meshes`): its creation stamp and size. A mesh
 * rewritten under its old name by the CLI, with no event, gets a new key on
 * the next mount.
 */
export function meshIdentity(info: { created_at?: string; vertex_count: number; face_count: number } | null | undefined): string {
  return info ? `${info.created_at ?? ''}|${info.vertex_count}|${info.face_count}` : '';
}

// --------------------------------------------------- byte estimates ----

/** Bytes of a decoded points page: the RUXP buffer, or the JSON rows (≈ 8 B per number plus array overhead). */
export function pageBytes(page: { format: 'binary' | 'json'; page: unknown }): number {
  if (page.format === 'binary') {
    const p = page.page as { fields?: { name: string }[]; view?: (n: string) => ArrayBufferView | null; count?: number };
    const name = p.fields?.[0]?.name;
    const view = name && p.view ? p.view(name) : null;
    if (view) return view.buffer.byteLength;
    return (p.count ?? 0) * 16;
  }
  const p = page.page as { count?: number; fields?: unknown[] };
  return (p.count ?? 0) * ((p.fields?.length ?? 4) * 8 + 48);
}

// ---------------------------------------------------- the instance ----

function deviceMemory(): number | undefined {
  try {
    return typeof navigator === 'undefined' ? undefined : (navigator as { deviceMemory?: number }).deviceMemory;
  } catch {
    return undefined;
  }
}

const cache = new ByteLru<unknown>(budgetFor(deviceMemory()));
const cameras = new Map<string, unknown>();
onScopeDropped = (scope) => {
  for (const key of [...cameras.keys()]) if (key.startsWith(`${scope}\u0000`)) cameras.delete(key);
};

/** The shared cache (tests and diagnostics). */
export function viewerCache(): ByteLru<unknown> {
  return cache;
}

/**
 * `fetch` with the responses kept in `store` under the case `scope` and the
 * stream `stream`. A hit resolves from memory; a miss fetches and stores (a
 * rejected fetch stores nothing). `scope` null bypasses the cache.
 */
export function cachedFetch<Q, V>(
  store: ByteLru<unknown>,
  scope: string | null,
  stream: string,
  tags: readonly string[],
  fetch: (name: string, query: Q) => Promise<V>,
  keyOf: (query: Q) => string,
  bytesOf: (value: V) => number,
): (name: string, query: Q) => Promise<V> {
  if (scope === null) return fetch;
  return async (name, query) => {
    const key = `${scope}\u0000${stream}\u0000${name}\u0000${keyOf(query)}`;
    const hit = store.get(key) as V | undefined;
    if (hit !== undefined) {
      // A replay must still stream: resolved in a microtask, every cached
      // page would be decoded and uploaded in one task, and nothing — not
      // even the first page — would paint until the last was in.
      await yieldToBrowser();
      return hit;
    }
    const generation = store.generation;
    const value = await fetch(name, query);
    if (store.generation === generation) store.set(key, value, bytesOf(value), { scope, group: `${scope}\u0000${stream}`, tags });
    return value;
  };
}

/** Lets the browser render and handle input between two cached pages. */
function yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/** Same as {@link cachedFetch}, for a value with no query (a tile index, a mesh). */
export async function cachedValue<V>(
  store: ByteLru<unknown>,
  scope: string | null,
  key: string,
  tags: readonly string[],
  load: () => Promise<V>,
  bytesOf: (value: V) => number,
): Promise<V> {
  if (scope === null) return load();
  const full = `${scope}\u0000${key}`;
  const hit = store.get(full) as V | undefined;
  if (hit !== undefined) return hit;
  const generation = store.generation;
  const value = await load();
  if (store.generation === generation) store.set(full, value, bytesOf(value), { scope, group: full, tags });
  return value;
}

// ------------------------------------------------------ invalidation ----

/** Drops everything streamed from, or zipped with, any of `names` (`clouds.changed`). */
export function invalidateClouds(names: readonly string[], store: ByteLru<unknown> = cache): number {
  if (names.length === 0) return 0;
  const set = new Set(names);
  return store.deleteWhere((e) => e.tags.some((t) => set.has(t)));
}

/** Drops every cached mesh (a finished job may have rewritten any of them under the same name). */
export function invalidateMeshes(store: ByteLru<unknown> = cache): number {
  return store.deleteWhere((e) => e.tags.some((t) => t.startsWith('mesh:')));
}

/**
 * Records the jobs in `jobs` that have finished; true when one finished that
 * `seen` did not know yet (the caller then drops the meshes: a job may have
 * rewritten one under the same name). The first call, with the jobs already
 * finished at connect, is harmless — the cache is empty then.
 */
export function noteFinishedJobs(seen: Set<string>, jobs: readonly { id: string; status: string }[]): boolean {
  let fresh = false;
  for (const job of jobs) {
    if (job.status !== 'succeeded' && job.status !== 'failed' && job.status !== 'cancelled') continue;
    if (seen.has(job.id)) continue;
    seen.add(job.id);
    fresh = true;
  }
  return fresh;
}

/** Drops everything, cameras too (an event-socket reconnect: events may have been missed). */
export function clearViewerCache(store: ByteLru<unknown> = cache): void {
  store.clear();
  if (store === cache) cameras.clear();
}

// ------------------------------------------------------------ camera ----

/** The orbit camera of a viewport, in a form that outlives its scene (`PointCloudScene.cameraSnapshot`). */
export interface CameraSnapshot {
  /** The scene's recentring origin (world); the poses below are scene-local to it. */
  origin: [number, number, number];
  projection: 'perspective' | 'orthographic';
  position: [number, number, number];
  target: [number, number, number];
  up: [number, number, number];
  orthoHalfHeight: number;
  orthoZoom: number;
}

function isVec3(v: unknown): v is [number, number, number] {
  return Array.isArray(v) && v.length === 3 && v.every((x) => typeof x === 'number' && Number.isFinite(x));
}

/** Whether `v` is a usable snapshot: every number finite, the up vector non-zero, the frustum positive. */
export function isCameraSnapshot(v: unknown): v is CameraSnapshot {
  if (!v || typeof v !== 'object') return false;
  const s = v as Record<string, unknown>;
  return (
    isVec3(s.origin) &&
    isVec3(s.position) &&
    isVec3(s.target) &&
    isVec3(s.up) &&
    (s.up as number[]).some((x) => x !== 0) &&
    (s.projection === 'perspective' || s.projection === 'orthographic') &&
    typeof s.orthoHalfHeight === 'number' &&
    s.orthoHalfHeight > 0 &&
    Number.isFinite(s.orthoHalfHeight) &&
    typeof s.orthoZoom === 'number' &&
    s.orthoZoom > 0 &&
    Number.isFinite(s.orthoZoom)
  );
}

/** Keeps (or with null, forgets) the camera of viewport `key` in case `scope`. */
export function rememberCamera(scope: string | null, key: string, snapshot: CameraSnapshot | null): void {
  if (scope === null) return;
  const full = `${scope}\u0000${key}`;
  if (snapshot && isCameraSnapshot(snapshot)) cameras.set(full, snapshot);
  else cameras.delete(full);
}

/** The camera viewport `key` had in case `scope`, or null. */
export function recalledCamera(scope: string | null, key: string): CameraSnapshot | null {
  if (scope === null) return null;
  const s = cameras.get(`${scope}\u0000${key}`);
  return isCameraSnapshot(s) ? s : null;
}
